import {
  useProtectedMutation,
  setUser,
  getUser,
  logout,
} from '@homeviews/store'
import React, { useEffect, useState } from 'react'
import type { RouteProps } from 'react-router-dom'
import { Route, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import {
  NAVIGATION,
  COMPANY_GROUP_ADMIN_VISIBLE_SLUGS,
} from '../constants/navigation'
import { HAS_PERMISSION, IS_COMPANY_GROUP_ADMIN } from '@homeviews/users'

// todo here set up permissions!

interface SectionRouteProps extends RouteProps {
  permissions?: any
  protectedRoute?: boolean
}

export const SectionRoute: React.FC<SectionRouteProps> = ({
  protectedRoute,
  path,
  ...props
}) => {
  const token = localStorage.getItem('homeviews_access_token')
  const [attemptAccess, { data, isLoading, isError }] = useProtectedMutation()
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const [whoamiRetries, setWhoamiRetries] = useState(0)

  useEffect(() => {
    if ((window as any)?.dataLayer) {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;(window as any)?.dataLayer.push({
        event: 'pageview',
      })
    }

    if (!user) {
      void attemptAccess()
    }
  }, [path, attemptAccess, user])

  useEffect(() => {
    if (data && !user) {
      dispatch(setUser({ user: data }))
    }
  }, [data, user, dispatch])

  // The first whoami after login can fail transiently (token not yet attached
  // to the very first request, a redirect/CORS hiccup, a flaky network). Retry
  // a few times before giving up so a still-valid session is not torn down.
  const MAX_WHOAMI_RETRIES = 3
  useEffect(() => {
    if (
      protectedRoute &&
      isError &&
      token &&
      !user &&
      whoamiRetries < MAX_WHOAMI_RETRIES
    ) {
      const timeout = setTimeout(() => {
        setWhoamiRetries((count) => count + 1)
        void attemptAccess()
      }, 400)
      return () => clearTimeout(timeout)
    }
  }, [protectedRoute, isError, token, user, whoamiRetries, attemptAccess])

  // Only once the retries above are exhausted do we treat the session as
  // genuinely invalid: clear the stale token and send the user to Login rather
  // than stranding them on a blank protected page.
  const whoamiFailed =
    protectedRoute && isError && !user && whoamiRetries >= MAX_WHOAMI_RETRIES

  useEffect(() => {
    if (whoamiFailed) {
      dispatch(logout())
    }
  }, [whoamiFailed, dispatch])

  if (protectedRoute) {
    if (!token) {
      return <Redirect to={NAVIGATION.login.slug} />
    }
    if (whoamiFailed) {
      return <Redirect to={NAVIGATION.login.slug} />
    }
    // Wait until the user (and therefore their role) is resolved before
    // mounting a protected route. A transient whoami error (still retrying)
    // also lands here, so we keep waiting rather than bouncing to Login.
    if (isLoading || isError || !user) {
      return <span />
    }
  }

  if (user) {
    // CompanyGroupAdmin users are restricted to read-only oversight pages.
    // Any protected route outside their allowlist redirects to the Dashboard.
    // `path` can be a string, an array of strings, or undefined, so normalise
    // it before checking the allowlist.
    const paths = Array.isArray(path) ? path : path ? [path] : []
    const isAllowedForGroupAdmin = paths.some((p) =>
      COMPANY_GROUP_ADMIN_VISIBLE_SLUGS.includes(p),
    )
    if (
      protectedRoute &&
      IS_COMPANY_GROUP_ADMIN(user) &&
      !isAllowedForGroupAdmin
    ) {
      return <Redirect to={NAVIGATION.dashboard.slug} />
    }

    let redirectHome = false

    if (
      (NAVIGATION.users.slug === path ||
        NAVIGATION.editUserPermissions.slug === path ||
        NAVIGATION.editUser.slug === path ||
        NAVIGATION.createUser.slug === path) &&
      !HAS_PERMISSION(user, 'EDIT_PERMISSIONS', 'global')
    ) {
      redirectHome = true
    } else if (
      NAVIGATION.companies.slug === path &&
      !HAS_PERMISSION(user, 'EDIT_COMPANY_PAGE', 'global')
    ) {
      redirectHome = true
    }

    if (redirectHome) {
      return <Redirect to="/" />
    }
  }

  return <Route path={path} {...props} />
}
export default SectionRoute
